import { Observable, Subject, BehaviorSubject, combineLatest, map } from 'rxjs';
import { inject } from '@angular/core';
import {
    HubConnection,
    HttpTransportType,
    HubConnectionBuilder,
    IHttpConnectionOptions,
    LogLevel,
} from '@microsoft/signalr';
import { SessionData } from '../../../auth/models/session-data.model';
import { APP_CONFIG } from '../../../../env/env.token';
import {
    SealedTradingEventsPort,
    SealedBidEventPayload,
    SealedAuctionLifecyclePayload,
    TradingProcedureStatusChanged,
    ConnectionStatus,
} from '@tradgine/sealed-auction-trade';
import { toTradingProcedureStatus } from '../mappers/mappers';
import { LocalStorageService } from '../../../../core/services/browser-storage/local-storage.service';
import { LocalStorageKeys } from '../../../../core/services/browser-storage/types/local-storage.enum';
import { TradingProcedureReportingHubService } from '../../../../core/hubs/trading-procedure-reporting-hub/trading-procedure-reporting.hub.service';
import { map as rxMap } from 'rxjs/operators';

export class SealedTradingEventsAdapter implements SealedTradingEventsPort {
    get bidPlaced$(): Observable<SealedBidEventPayload> {
        return this._bidPlaced$.asObservable();
    }
    private _bidPlaced$ = new Subject<SealedBidEventPayload>();

    get bidUpdated$(): Observable<SealedBidEventPayload> {
        return this._bidUpdated$.asObservable();
    }
    private _bidUpdated$ = new Subject<SealedBidEventPayload>();

    get bidDeleted$(): Observable<SealedBidEventPayload> {
        return this._bidDeleted$.asObservable();
    }
    private _bidDeleted$ = new Subject<SealedBidEventPayload>();

    get auctionStarted$(): Observable<SealedAuctionLifecyclePayload> {
        return this._auctionStarted$.asObservable();
    }
    private _auctionStarted$ = new Subject<SealedAuctionLifecyclePayload>();

    get auctionEnded$(): Observable<SealedAuctionLifecyclePayload> {
        return this._auctionEnded$.asObservable();
    }
    private _auctionEnded$ = new Subject<SealedAuctionLifecyclePayload>();

    get tradingProcedureStatusChanged$(): Observable<TradingProcedureStatusChanged> {
        return this.tradingProcedureStatusReportingHubService.tradingProcedureStatusChanged$.pipe(
            rxMap((msg) => ({
                id: msg.id,
                status: toTradingProcedureStatus(msg.status),
            }))
        );
    }

    get reconnected$(): Observable<void> {
        return this._reconnected$.asObservable();
    }
    private _reconnected$ = new Subject<void>();

    get connectionStatus$(): Observable<ConnectionStatus> {
        return this._connectionStatus$;
    }
    private _bidHubStatus$ = new BehaviorSubject<ConnectionStatus>('connecting');
    private _auctionHubStatus$ = new BehaviorSubject<ConnectionStatus>('connecting');
    private _connectionStatus$ = combineLatest([this._bidHubStatus$, this._auctionHubStatus$]).pipe(
        map(([bid, auction]): ConnectionStatus => {
            if (bid === 'disconnected' && auction === 'disconnected') return 'disconnected';
            if (bid === 'reconnecting' || auction === 'reconnecting') return 'reconnecting';
            if (bid === 'connecting' || auction === 'connecting') return 'connecting';
            return 'connected';
        })
    );

    private readonly localStorage = inject(LocalStorageService);
    private readonly appConfig = inject(APP_CONFIG);
    private readonly tradingProcedureStatusReportingHubService = inject(TradingProcedureReportingHubService);

    private readonly sealedBidReportConnection: HubConnection;
    private readonly auctionReportConnection: HubConnection;

    constructor() {
        const options: IHttpConnectionOptions = {
            transport: HttpTransportType.WebSockets,
            skipNegotiation: true,
            accessTokenFactory: () => this.localStorage.getItem<SessionData>(LocalStorageKeys.SessionData)?.accessToken,
        };

        const logLevel = this.appConfig.environment === 'Production' ? LogLevel.Error : LogLevel.Information;

        this.sealedBidReportConnection = new HubConnectionBuilder()
            .withUrl(this.appConfig.sealedBidReportingHubUrl, options)
            .withAutomaticReconnect()
            .configureLogging(logLevel)
            .build();

        this.auctionReportConnection = new HubConnectionBuilder()
            .withUrl(this.appConfig.auctionReportingHubUrl, options)
            .withAutomaticReconnect()
            .configureLogging(logLevel)
            .build();

        // Connection lifecycle hooks — регистрируются один раз в конструкторе
        this.sealedBidReportConnection.onreconnecting(() => this._bidHubStatus$.next('reconnecting'));
        this.sealedBidReportConnection.onreconnected(() => {
            this._bidHubStatus$.next('connected');
            this._reconnected$.next();
        });
        this.sealedBidReportConnection.onclose(() => this._bidHubStatus$.next('disconnected'));

        this.auctionReportConnection.onreconnecting(() => this._auctionHubStatus$.next('reconnecting'));
        this.auctionReportConnection.onreconnected(() => {
            this._auctionHubStatus$.next('connected');
            this._reconnected$.next();
        });
        this.auctionReportConnection.onclose(() => this._auctionHubStatus$.next('disconnected'));

        // Регистрируем обработчики событий один раз в конструкторе
        this.registerHandlers();
    }

    stopConnections(): void {
        this.sealedBidReportConnection.stop();
        this.auctionReportConnection.stop();
    }

    startConnections(): void {
        this._bidHubStatus$.next('connecting');
        this._auctionHubStatus$.next('connecting');

        this.sealedBidReportConnection
            .start()
            .then(() => this._bidHubStatus$.next('connected'))
            .catch((err) => {
                console.log('[SealedTradingEventsAdapter] bid hub connection error', err);
                this._bidHubStatus$.next('disconnected');
            });

        this.auctionReportConnection
            .start()
            .then(() => this._auctionHubStatus$.next('connected'))
            .catch((err) => {
                console.log('[SealedTradingEventsAdapter] auction hub connection error', err);
                this._auctionHubStatus$.next('disconnected');
            });
    }

    private registerHandlers(): void {
        this.sealedBidReportConnection.on('ReportSealedBidCreated', (args: { auctionId: number }) => {
            const payload = {
                auctionId: args.auctionId,
                dateTime: new Date(),
            };
            this._bidPlaced$.next(payload);
            this._bidUpdated$.next(payload);
        });

        this.sealedBidReportConnection.on('ReportSealedBidDeleted', (args: { auctionId: number }) => {
            this._bidDeleted$.next({
                auctionId: args.auctionId,
                dateTime: new Date(),
            });
        });

        this.auctionReportConnection.on('ReportAuctionStarted', (args) => {
            this._auctionStarted$.next({
                auctionId: args.id,
                lotId: args.id,
                dateTime: new Date(args?.dateTime || Date.now()),
                endDateTime: args?.endDateTime ? new Date(args.endDateTime) : undefined,
            });
        });

        this.auctionReportConnection.on('ReportAuctionEnded', (args) => {
            this._auctionEnded$.next({
                auctionId: args.id,
                lotId: args.id,
                dateTime: new Date(args?.dateTime || Date.now()),
                leadingSealedBidId: args.leadingSealedBidId ?? null,
            });
        });
    }
}
